import {createRef, forwardRef, MutableRefObject, useEffect, useRef, useState} from "react";
import {UserId} from "../../cdx-models/User";
import {cameraToWorld, TwoNums, VisionBoardBag} from "./canvas-utils";
import {throttle} from "lodash";
import DSAvatar, {getHueValFromId} from "../../components/DSAvatar/DSAvatar";
import {useInstance} from "../../lib/mate/mate-utils";
import {Box, css} from "@cdx/ds";
import useWindowFocus from "@cdx/common/hooks/useWindowFocus";

const Cursor = forwardRef<HTMLDivElement, {id: UserId; initialPos: TwoNums}>(
  ({id, initialPos}, ref) => {
    const user = useInstance("user", id);
    return (
      <Box
        absolute
        zIndex={1}
        top="0"
        left="0"
        style={{
          transform: `translate(${initialPos[0]}px, ${initialPos[1]}px)`,
          transition: `all 0.2s linear`,
        }}
        ref={ref}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          width="8"
          height="8"
          viewBox="0 0 4 4"
          className={css({position: "absolute", top: "-2px", left: "-2px"})}
        >
          <path fill={`hsl(${getHueValFromId(id)}, 96%, 46%)`} d="M1 4 0 0l4 1-2 1-1 2Z" />
        </svg>
        <DSAvatar
          user={user}
          size={20}
          className={css({position: "absolute", top: "2px", left: "2px"})}
        />
      </Box>
    );
  }
);

type CursorRefEntry = {
  id: UserId;
  pos: [number, number];
  nodeRef: MutableRefObject<null | HTMLDivElement>;
};

const Cursors = ({meId, bag}: {meId: UserId; bag: VisionBoardBag}) => {
  const [cursorRefs, setCursorRefs] = useState<{map: Map<number, CursorRefEntry>}>(() => ({
    map: new Map(),
  }));
  const stateRef = useRef(cursorRefs);
  stateRef.current = cursorRefs;
  const windowFocus = useWindowFocus();

  useEffect(() => {
    const unsubs: (() => void)[] = [];
    const unsub = bag.awarenessStore.registerCursorListener((cursors) => {
      let needUpdate = false;
      const prev = stateRef.current.map;
      for (const [clientId, cursor] of cursors) {
        const exist = prev.get(clientId);
        if (!exist) {
          needUpdate = true;
          prev.set(clientId, {id: cursor.id, pos: cursor.pos, nodeRef: createRef()});
        } else {
          const node = exist.nodeRef.current;
          if (node && (exist.pos[0] !== cursor.pos[0] || exist.pos[1] !== cursor.pos[1])) {
            node.style.transform = `translate(${cursor.pos[0]}px, ${cursor.pos[1]}px)`;
            exist.pos = cursor.pos;
          }
        }
      }
      if (prev.size !== cursors.size) {
        for (const existingId of prev.keys()) {
          if (!cursors.has(existingId)) {
            needUpdate = true;
            prev.delete(existingId);
          }
        }
      }
      if (needUpdate) {
        setCursorRefs({map: prev});
      }
    });
    unsubs.push(unsub);
    if (windowFocus) {
      const handler = throttle((e: MouseEvent) => {
        bag.awarenessStore.setCursor(meId, cameraToWorld([e.pageX, e.pageY], bag.posRef));
      }, 200);
      window.addEventListener("mousemove", handler);
      unsubs.push(() => {
        window.removeEventListener("mousemove", handler);
        bag.awarenessStore.setCursor(meId, null);
      });
    }
    return () => {
      unsubs.forEach((fn) => fn());
    };
  }, [bag.awarenessStore, meId, bag.posRef, windowFocus]);

  return (
    <>
      {[...cursorRefs.map.entries()].map(([key, {id, pos: initialPos, nodeRef}]) => (
        <Cursor id={id} initialPos={initialPos} key={key} ref={nodeRef} />
      ))}
    </>
  );
};

export default Cursors;
